import { useCallback, useState } from 'react';
import { PendingCASummaryBanner } from './PendingCASummaryBanner';
import { PendingCAFilterBar } from './PendingCAFilterBar';
import { PendingCATable } from './PendingCATable';
import { PendingCABulkActions } from './PendingCABulkActions';
import { PendingCADetailModal } from './PendingCADetailModal';
import {
  useApproveMutation,
  useAnnotationMutation,
  useBulkApproveMutation,
  useEditApproveMutation,
  usePendingCAList,
  useRejectMutation,
  type ToastFn,
} from './usePendingCa';
import { DEFAULT_FILTERS, type PendingCAFilters, type PendingCAItem } from './pendingCa.types';
import { Pager } from '../../components/Pager';
import './pendingCa.css';

// 권위: docs/ARCH_DASHBOARD.md §4.10 — 화면 10 컨테이너.
//
// 레이아웃: 요약 배너 → 필터 바 → 테이블 → 일괄 처리 툴바 → (모달)
// 실시간: /topic/pending-ca 구독 (usePendingCAList 내부) + 폴링 폴백 30s
// 색상 규약: PendingCATable 의 rowClass() 가 적용

interface ToastItem {
  id: string;
  msg: string;
  tone: 'info' | 'success' | 'error' | 'warn';
}

interface ConfirmDialog {
  title: string;
  message: string;
  onConfirm: () => void;
}

export default function PendingCAEventsPage(): JSX.Element {
  const [filters, setFilters] = useState<PendingCAFilters>(DEFAULT_FILTERS);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [openAuditId, setOpenAuditId] = useState<number | null>(null);
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [confirm, setConfirm] = useState<ConfirmDialog | null>(null);
  // 행 단위 액션이 진행 중인 audit_id — 중복 클릭 방지.
  const [pendingRowIds, setPendingRowIds] = useState<Set<number>>(new Set());

  const pushToast: ToastFn = useCallback((msg, tone = 'info') => {
    const id = Math.random().toString(36).slice(2);
    setToasts((prev) => [...prev, { id, msg, tone }]);
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4500);
  }, []);

  const list = usePendingCAList(filters, pushToast);
  const items = list.data?.items ?? [];

  const approve = useApproveMutation(pushToast);
  const reject = useRejectMutation(pushToast);
  const editApprove = useEditApproveMutation(pushToast);
  const annotate = useAnnotationMutation(pushToast);
  const bulk = useBulkApproveMutation(pushToast);

  const busy =
    approve.isPending ||
    reject.isPending ||
    editApprove.isPending ||
    annotate.isPending ||
    bulk.isPending;

  // ── 선택 관리 ────────────────────────────────────────
  const onToggleSelect = useCallback((auditId: number): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(auditId)) next.delete(auditId);
      else next.add(auditId);
      return next;
    });
  }, []);

  const onToggleSelectAll = useCallback(
    (checked: boolean): void => {
      if (!checked) {
        setSelectedIds(new Set());
        return;
      }
      setSelectedIds(new Set(items.map((i) => i.auditId)));
    },
    [items]
  );

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  // ── 행 단위 pending 추적 ────────────────────────────
  const markRowPending = useCallback((auditId: number) => {
    setPendingRowIds((prev) => {
      if (prev.has(auditId)) return prev;
      const next = new Set(prev);
      next.add(auditId);
      return next;
    });
  }, []);
  const clearRowPending = useCallback((auditId: number) => {
    setPendingRowIds((prev) => {
      if (!prev.has(auditId)) return prev;
      const next = new Set(prev);
      next.delete(auditId);
      return next;
    });
  }, []);

  // ── 액션 핸들러 ──────────────────────────────────────
  // 행 단위 승인/반려 는 확인 다이얼로그 없이 즉시 API 호출 — TanStack Query 의
  // invalidateQueries 가 useApproveMutation / useRejectMutation 의 onSuccess 안에서
  // 실행되므로 성공 시 목록이 자동 refetch 된다.
  const onApprove = useCallback(
    (item: PendingCAItem) => {
      if (pendingRowIds.has(item.auditId)) return;
      markRowPending(item.auditId);
      approve.mutate(item.auditId, {
        onSettled: () => clearRowPending(item.auditId),
      });
    },
    [approve, clearRowPending, markRowPending, pendingRowIds]
  );

  const onReject = useCallback(
    (item: PendingCAItem) => {
      if (pendingRowIds.has(item.auditId)) return;
      // 인라인 반려 — 사유 입력 없이 빈 note 로 즉시 반려. 상세 사유가 필요하면
      // 행을 클릭해 상세 모달에서 사유를 입력할 수 있다 (섹션 D).
      markRowPending(item.auditId);
      reject.mutate(
        { auditId: item.auditId, note: '' },
        { onSettled: () => clearRowPending(item.auditId) }
      );
    },
    [clearRowPending, markRowPending, pendingRowIds, reject]
  );

  const onBulkSelected = useCallback(() => {
    if (selectedIds.size === 0) return;
    setConfirm({
      title: '선택 일괄 승인',
      message: `선택된 ${selectedIds.size} 건을 일괄 승인합니다. 개별 트랜잭션으로 처리되며 일부 실패해도 나머지는 진행됩니다. 계속하시겠습니까?`,
      onConfirm: () => {
        bulk.mutate(
          { auditIds: Array.from(selectedIds) },
          {
            onSuccess: () => {
              clearSelection();
            },
          }
        );
        setConfirm(null);
      },
    });
  }, [bulk, clearSelection, selectedIds]);

  const onBulkHighConfidence = useCallback(() => {
    setConfirm({
      title: '고신뢰 일괄 승인',
      message:
        'confidence ≥ 0.90 + status pending_review|auto_accepted 조건의 audit 를 일괄 승인합니다. (안전 상한 500 건). 계속하시겠습니까?',
      onConfirm: () => {
        bulk.mutate({
          filter: {
            confidenceMin: 0.9,
            reviewStatus: 'pending_review,auto_accepted',
          },
        });
        setConfirm(null);
      },
    });
  }, [bulk]);

  const total = list.data?.total ?? 0;

  return (
    <div className="pca-page">
      <PendingCASummaryBanner
        summary={list.data?.summary ?? null}
        onBulkHighConfidence={onBulkHighConfidence}
        bulkBusy={bulk.isPending}
      />

      <PendingCAFilterBar filters={filters} onChange={setFilters} />

      {list.isError && (
        <div className="pca-warning-banner">
          목록 로드 실패: {(list.error as Error).message}
        </div>
      )}

      <PendingCATable
        items={items}
        selectedIds={selectedIds}
        highlightedIds={list.highlightedIds}
        pendingRowIds={pendingRowIds}
        isLoading={list.isLoading}
        onToggleSelect={onToggleSelect}
        onToggleSelectAll={onToggleSelectAll}
        onRowClick={(item) => setOpenAuditId(item.auditId)}
        onApprove={onApprove}
        onReject={onReject}
      />

      <PendingCABulkActions
        selectedCount={selectedIds.size}
        onBulkApprove={onBulkSelected}
        onClearSelection={clearSelection}
        busy={bulk.isPending}
      />

      <Pager
        page={filters.page}
        pageSize={filters.size}
        total={total}
        onPageChange={(p) => setFilters({ ...filters, page: p })}
      />


      <PendingCADetailModal
        auditId={openAuditId}
        onClose={() => setOpenAuditId(null)}
        busy={busy}
        onApprove={(id) => {
          approve.mutate(id, {
            onSuccess: () => setOpenAuditId(null),
          });
        }}
        onReject={(id, note) => {
          reject.mutate(
            { auditId: id, note },
            { onSuccess: () => setOpenAuditId(null) }
          );
        }}
        onEditApprove={(id, extracted, note) => {
          editApprove.mutate(
            { auditId: id, extractedEvent: extracted, note },
            { onSuccess: () => setOpenAuditId(null) }
          );
        }}
        onAnnotate={(id, note) => {
          annotate.mutate({ auditId: id, note });
        }}
      />

      {confirm && (
        <div
          className="pca-modal__backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) setConfirm(null);
          }}
        >
          <div className="pca-confirm" role="dialog" aria-label="confirm">
            <h4 className="pca-confirm__title">{confirm.title}</h4>
            <div className="pca-confirm__msg">{confirm.message}</div>
            <div className="pca-confirm__actions">
              <button
                type="button"
                className="pca-btn pca-btn--ghost"
                onClick={() => setConfirm(null)}
              >
                취소
              </button>
              <button
                type="button"
                className="pca-btn pca-btn--primary"
                onClick={() => confirm.onConfirm()}
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="pca-toast-stack" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`pca-toast pca-toast--${t.tone}`}>
            {t.msg}
          </div>
        ))}
      </div>
    </div>
  );
}
